import React from 'react';
import { 
    X, Phone, Mail, MapPin, Clock, HelpCircle, ExternalLink
} from 'lucide-react';

/**
 * Help Info Modal - Shows organization-specific contact information
 * Displays different content for Elysian (residential) vs Burston (commercial)
 */
export function HelpInfoModal({ isOpen, onClose, organization }) {
    if (!isOpen) return null;

    const isElysian = organization?.slug === 'elysian' || organization?.name?.toLowerCase().includes('elysian');
    const isBurston = organization?.slug === 'burston' || organization?.name?.toLowerCase().includes('burston');

    // Organization-specific info
    const orgInfo = isElysian ? {
        name: 'Elysian Residences',
        tagline: 'Premium Retirement Living',
        phone: '07957 743331',
        email: 'hello@elysianresidences.co.uk',
        address: 'The Oakleigh, St Albans, Hertfordshire',
        openingHours: [
            { venue: 'Reception & Concierge', hours: '24 hours, 7 days' },
            { venue: 'Restaurant', hours: '7:30am - 9:00pm daily' },
            { venue: 'Wellness Centre', hours: '6:30am - 9:00pm' },
            { venue: 'Bar & Lounge', hours: '10:00am - 10:00pm' },
        ],
        quickLinks: [
            { label: 'Request Maintenance', path: '/services' },
            { label: 'Book Dining', path: '/dining' },
            { label: 'View Activities', path: '/activities' },
        ],
        themeColor: 'stone'
    } : {
        name: 'Burston Garden Centre',
        tagline: 'Growing Inspiration Since 1978',
        phone: '01727 832444',
        email: 'info@burston.co.uk',
        address: 'North Orbital Road, St Albans, Hertfordshire, AL2 2DS',
        website: 'www.burston.co.uk',
        openingHours: [
            { venue: 'Garden Centre', hours: '9:00am - 5:30pm Mon-Sat, 10:30am - 4:30pm Sun' },
            { venue: 'Restaurant', hours: '9:00am - 4:30pm daily' },
            { venue: 'Breakfast', hours: 'Until 11:30am' },
            { venue: 'Hot Meals', hours: '11:30am - 3:00pm' },
        ],
        quickLinks: [
            { label: 'View Events', path: '/events' },
            { label: 'See Offers', path: '/offers' },
            { label: 'Restaurant Menu', path: '/dining' },
        ],
        themeColor: 'emerald'
    };

    const themeClasses = orgInfo.themeColor === 'stone' 
        ? {
            bg: 'bg-stone-600',
            bgLight: 'bg-stone-50',
            border: 'border-stone-200',
            text: 'text-stone-700',
            accent: 'text-stone-600'
          }
        : {
            bg: 'bg-emerald-600',
            bgLight: 'bg-emerald-50',
            border: 'border-emerald-200',
            text: 'text-emerald-700',
            accent: 'text-emerald-600'
          };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-300">
                {/* Header */}
                <div className={`${themeClasses.bg} px-6 py-5 text-white`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold">{orgInfo.name}</h2>
                            <p className="text-white/70 text-sm mt-0.5">{orgInfo.tagline}</p>
                        </div>
                        <button 
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                
                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
                    {/* Contact Info */}
                    <div className="space-y-3">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Phone className={`w-4 h-4 ${themeClasses.accent}`} />
                            Contact Us
                        </h3>
                        
                        <a 
                            href={`tel:${orgInfo.phone.replace(/\s/g, '')}`}
                            className={`flex items-center gap-3 p-4 ${themeClasses.bgLight} rounded-xl ${themeClasses.border} border hover:shadow-md transition-all`}
                        >
                            <div className={`w-10 h-10 rounded-full ${themeClasses.bg} flex items-center justify-center`}>
                                <Phone className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900">{orgInfo.phone}</p>
                                <p className="text-sm text-gray-500">Tap to call</p>
                            </div>
                        </a>
                        
                        <a 
                            href={`mailto:${orgInfo.email}`}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-all"
                        >
                            <Mail className="w-5 h-5 text-gray-500" />
                            <span className="text-gray-700">{orgInfo.email}</span>
                        </a>
                        
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">{orgInfo.address}</span>
                        </div>

                        {orgInfo.website && (
                            <a 
                                href={`https://${orgInfo.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-all"
                            >
                                <ExternalLink className="w-5 h-5 text-gray-500" />
                                <span className="text-gray-700">{orgInfo.website}</span>
                            </a>
                        )}
                    </div>
                    
                    {/* Opening Hours */}
                    <div className="space-y-3">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className={`w-4 h-4 ${themeClasses.accent}`} />
                            Opening Hours
                        </h3>
                        <div className="space-y-2">
                            {orgInfo.openingHours.map((item, index) => (
                                <div 
                                    key={index}
                                    className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                                >
                                    <span className="text-gray-700 font-medium text-sm">{item.venue}</span>
                                    <span className="text-gray-500 text-sm">{item.hours}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Free Parking Notice (Burston only) */}
                    {isBurston && (
                        <div className={`p-4 ${themeClasses.bgLight} rounded-xl ${themeClasses.border} border text-center`}>
                            <p className={`font-semibold ${themeClasses.text}`}>FREE Parking Available</p>
                            <p className="text-sm text-gray-500 mt-1">Plenty of spaces for all visitors</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default HelpInfoModal;
